import { FC } from 'react'
import classnames from 'classnames'
import Input from './input'
import DatePickerButton from './DatePickerButton'
import Label from './label'

export type ChangeEventParams = {
  name: string
  value: {
    day: string
    month: string
    year: string
  }
}

type DateInputProps = {
  value: ChangeEventParams['value']
  onInputChange: (value: ChangeEventParams) => void
  onCalendarChange: (value: ChangeEventParams) => void
  id: string
  name: string
  className?: string
  dateFormat?: 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD'
  isBelowYearOnlyCutOff: boolean
  isBce: boolean
}

type InputEvent = {
  name: string
  value: string
  checked?: boolean
}

type InputRowProps = {
  id: string
  name: string
  value: string
  onChange: (e: InputEvent) => void
  placeholder: string
  label: string
  disabled?: boolean
  step?: string
  className?: string
}

const componentClass = 'date-picker-input'

const InputRow: FC<InputRowProps> = ({ id, name, value, onChange, placeholder, label, step, className, disabled }) => {
  const cssClass = classnames(`${componentClass}__item`, className)
  return (
    <div className={cssClass}>
      <Label htmlFor={id} className={`${componentClass}__label`}>
        {label}
      </Label>
      <Input
        step={step}
        id={id}
        name={name}
        type="number"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`input ${componentClass}__field`}
        disabled={disabled}
      />
    </div>
  )
}

const DateInput: FC<DateInputProps> = ({
  id,
  name,
  onInputChange,
  onCalendarChange,
  className,
  value,
  dateFormat = 'MM-DD-YYYY',
  isBce,
  isBelowYearOnlyCutOff,
}) => {
  const cssClass = classnames(componentClass, className)

  const handleInputChange = (e: InputEvent, key: keyof ChangeEventParams['value']) =>
    onInputChange({
      name,
      value: {
        ...value,
        [key]: e.value,
      },
    })

  const handleDatePickerChange = (dateString: string) => {
    const newVals = dateString.split('-') // format is Y-m-d
    onCalendarChange({
      name,
      value: {
        year: newVals[0],
        month: newVals[1],
        day: newVals[2],
      },
    })
  }

  const dayId = `${id}-day`
  const monthId = `${id}-month`
  const yearId = `${id}-year`

  const { day, month, year } = value
  const calendarValue = day && month && year ? `${value.year}-${value.month}-${value.day}` : ''

  return (
    <div className={cssClass}>
      {dateFormat.split('-').map((type: string) => {
        if (type === 'DD') {
          return (
            <InputRow
              key={type}
              id={dayId}
              name={name}
              label="Day"
              onChange={e => handleInputChange(e, 'day')}
              placeholder="dd"
              value={value?.day}
              disabled={isBelowYearOnlyCutOff}
            />
          )
        } else if (type === 'MM') {
          return (
            <InputRow
              key={type}
              id={monthId}
              name={name}
              label="Month"
              onChange={e => handleInputChange(e, 'month')}
              placeholder="mm"
              value={value?.month}
              disabled={isBelowYearOnlyCutOff}
            />
          )
        }
        return (
          <InputRow
            key={type}
            id={yearId}
            className={`${componentClass}__item--year`}
            name={name}
            label="Year"
            onChange={e => handleInputChange(e, 'year')}
            placeholder="yyyy"
            value={value?.year}
          />
        )
      })}

      <DatePickerButton isBce={isBce} value={calendarValue} onChange={handleDatePickerChange} />
    </div>
  )
}

export default DateInput
